'use client'

import { forwardRef, useId } from 'react'

import { cn } from '@/core/utils/common'

import type { TextareaHTMLAttributes } from 'react'

type TextareaVariant = 'outline' | 'underline'

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string
  error?: string
  helperText?: string
  variant?: TextareaVariant
  classNames?: {
    container?: string
    textarea?: string
    label?: string
    error?: string
  }
}

const variantStyles: Record<TextareaVariant, string> = {
  outline:
    'rounded-field border border-border bg-background px-4 py-3 focus:border-point',
  underline:
    'border-b border-border bg-transparent px-1 py-3 focus:border-point',
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'outline',
      id,
      className,
      classNames,
      'aria-describedby': externalDescription,
      'aria-invalid': externalInvalid,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const textareaId = id ?? generatedId
    const helperTextId = helperText ? `${textareaId}-helper` : undefined
    const errorId = error ? `${textareaId}-error` : undefined
    const describedBy =
      [externalDescription, errorId ?? helperTextId].filter(Boolean).join(' ') ||
      undefined

    return (
      <div className={cn(classNames?.container)}>
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              'mb-2 block text-c14 font-medium text-foreground',
              classNames?.label
            )}
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            'min-h-28 w-full resize-y text-c15 text-foreground outline-none',
            'transition-[color,background-color,border-color] duration-200',
            'placeholder:text-description/70',
            variantStyles[variant],
            error && 'border-error focus:border-error',
            classNames?.textarea,
            className
          )}
          aria-invalid={error ? true : externalInvalid}
          aria-describedby={describedBy}
          {...props}
        />
        {helperText && !error && (
          <p
            id={helperTextId}
            className="mt-2 text-c12 leading-5 text-description"
          >
            {helperText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className={cn(
              'mt-2 text-c12 leading-5 text-error',
              classNames?.error
            )}
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
